// ============================================================
// TaskFlow OS - Plugins Page
// ============================================================

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Header from '../components/layout/Header';
import { pluginRegistry } from '../plugins/plugin-system';
import { IconPlugins, IconZap, IconCpu, IconCheck, IconX } from '../icons/SvgIcons';

const Plugins: React.FC = () => {
  const [plugins, setPlugins] = useState(pluginRegistry.getAll());

  const togglePlugin = (name: string) => {
    const plugin = pluginRegistry.get(name);
    if (!plugin) return;

    if (plugin.enabled) {
      pluginRegistry.disable(name);
    } else {
      pluginRegistry.enable(name);
    }
    setPlugins(pluginRegistry.getAll());
  };

  return (
    <>
      <Header title="プラグイン管理" subtitle="機能の追加・管理" />

      <div className="p-6 max-w-4xl mx-auto">
        {/* Info Banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card mb-6"
        >
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-purple-500/15 border border-purple-500/25 flex items-center justify-center shrink-0">
              <IconCpu size={18} color="#8b5cf6" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-white mb-1">拡張可能なアーキテクチャ</h3>
              <p className="text-xs text-white/40">
                プラグインシステムにより、AI機能・CRM連携・経理連携など、必要な機能を後から追加できます。
                イベント駆動設計により、すべての操作をフックして拡張処理を実行します。
              </p>
            </div>
          </div>
        </motion.div>

        {/* Plugin Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {plugins.map((plugin, idx) => (
            <motion.div
              key={plugin.manifest.name}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.05 }}
              className="glass-card"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                    plugin.enabled
                      ? 'bg-emerald-500/15 border border-emerald-500/25'
                      : 'bg-white/5 border border-white/10'
                  }`}>
                    {plugin.manifest.name.includes('ai') ? (
                      <IconCpu size={18} color={plugin.enabled ? '#10b981' : '#6b7280'} />
                    ) : (
                      <IconPlugins size={18} color={plugin.enabled ? '#10b981' : '#6b7280'} />
                    )}
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-white">{plugin.manifest.displayName}</h3>
                    <span className="text-[10px] text-white/30">v{plugin.manifest.version} by {plugin.manifest.author}</span>
                  </div>
                </div>

                {/* Toggle */}
                <button
                  onClick={() => togglePlugin(plugin.manifest.name)}
                  className={`relative w-12 h-6 rounded-full transition-colors ${
                    plugin.enabled ? 'bg-emerald-500/30' : 'bg-white/10'
                  }`}
                >
                  <div className={`absolute top-0.5 w-5 h-5 rounded-full transition-all ${
                    plugin.enabled
                      ? 'left-6 bg-emerald-400'
                      : 'left-0.5 bg-white/30'
                  }`} />
                </button>
              </div>

              <p className="text-xs text-white/40 mb-3">{plugin.manifest.description}</p>

              {/* Hooks */}
              {plugin.manifest.hooks.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {plugin.manifest.hooks.map((hook) => (
                    <span key={hook} className="px-2 py-0.5 rounded-full text-[9px] bg-white/5 text-white/30 border border-white/5">
                      {hook}
                    </span>
                  ))}
                </div>
              )}

              {/* Status */}
              <div className="mt-3 pt-3 border-t border-white/5 flex items-center gap-2">
                {plugin.enabled ? (
                  <span className="flex items-center gap-1 text-[10px] text-emerald-400">
                    <IconCheck size={10} /> 有効
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-[10px] text-white/30">
                    <IconX size={10} /> 無効
                  </span>
                )}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Future Plugins */}
        <h3 className="text-sm font-semibold text-white/60 mt-8 mb-4">今後追加予定</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { name: 'AI ワークフロー生成', desc: 'GPT/Claude等によるインテリジェントなワークフロー自動生成' },
            { name: 'CRM 連携', desc: 'Salesforce / HubSpot 等との双方向データ同期' },
            { name: '経理連携', desc: '予算管理・請求管理とのシームレスな統合' },
          ].map((item, idx) => (
            <motion.div
              key={item.name}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 + idx * 0.05 }}
              className="glass-card opacity-60"
            >
              <div className="flex items-center gap-2 mb-2">
                <IconZap size={14} className="text-white/20" />
                <h4 className="text-xs font-medium text-white/60">{item.name}</h4>
              </div>
              <p className="text-[10px] text-white/30">{item.desc}</p>
              <div className="mt-2">
                <span className="text-[9px] px-2 py-0.5 rounded-full bg-white/5 text-white/20 border border-white/5">
                  開発予定
                </span>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </>
  );
};

export default Plugins;
